// src/pages/Drivers.jsx
import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { driversService } from "../../services";
import "./Drivers.css";

// Status options
const STATUS_OPTIONS = [
  { value: '', label: 'All Status' },
  { value: 'Online', label: 'Online' },
  { value: 'Offline', label: 'Offline' },
  { value: 'Delayed', label: 'Delayed' },
];

// Status badge helper
const getStatusClass = (status) => {
  const statusMap = {
    'Online': 'drivers-status-online',
    'Offline': 'drivers-status-offline',
    'Delayed': 'drivers-status-delayed',
  };
  return statusMap[status] || 'drivers-status-offline';
};

export default function Drivers() {
  const navigate = useNavigate();

  // State
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [drivers, setDrivers] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1, total: 0 });
  const [stats, setStats] = useState({ online: 0, offline: 0, delayed: 0, total: 0, onTask: 0, available: 0 });

  // Filters
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  // Modal states
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [addForm, setAddForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    phone: '',
    vehicle: '',
    licenseNumber: '',
  });
  const [saving, setSaving] = useState(false);

  // Menu state
  const [activeMenu, setActiveMenu] = useState(null);

  // Fetch drivers with job stats
  const fetchDrivers = useCallback(async (page = 1) => {
    try {
      setLoading(true);
      setError(null);

      const filters = {
        page,
        limit: 10,
        search: search || undefined,
        status: statusFilter || undefined,
      };

      // Use the enhanced API that includes job stats
      const response = await driversService.getDriversWithJobs(filters);

      setDrivers(response.drivers || []);
      setPagination({
        page: response.pagination?.page || page,
        totalPages: response.pagination?.totalPages || 1,
        total: response.pagination?.total || 0,
      });

      // Calculate stats from response
      const allDrivers = response.drivers || [];
      const onlineDrivers = allDrivers.filter(d => d.driverProfile?.status === 'Online');
      const onTaskDrivers = allDrivers.filter(d => d.jobStats?.activeJobs > 0);
      const availableDrivers = onlineDrivers.filter(d => !d.jobStats?.activeJobs);

      setStats({
        online: onlineDrivers.length,
        offline: allDrivers.filter(d => d.driverProfile?.status === 'Offline' || !d.driverProfile?.status).length,
        delayed: allDrivers.filter(d => d.driverProfile?.status === 'Delayed').length,
        total: response.pagination?.total || allDrivers.length,
        onTask: onTaskDrivers.length,
        available: availableDrivers.length,
      });

    } catch (err) {
      console.error('Fetch drivers error:', err);
      setError('Failed to load drivers');
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter]);

  // Fetch on mount and when filters change
  useEffect(() => {
    const debounce = setTimeout(() => {
      fetchDrivers(1);
    }, 300);
    return () => clearTimeout(debounce);
  }, [fetchDrivers]);

  // Handle update status
  const handleUpdateStatus = async (driverId, newStatus) => {
    try {
      await driversService.updateStatus(driverId, newStatus);
      setActiveMenu(null);
      fetchDrivers(pagination.page);
    } catch (err) {
      console.error('Update status error:', err);
      alert('Failed to update status');
    }
  };

  // Handle delete/deactivate driver
  const handleDeleteDriver = async (driverId) => {
    if (!window.confirm('Are you sure you want to deactivate this driver?')) return;

    try {
      await driversService.deleteDriver(driverId);
      setActiveMenu(null);
      fetchDrivers(pagination.page);
    } catch (err) {
      console.error('Delete driver error:', err);
      alert('Failed to deactivate driver');
    }
  };

  // Navigate to driver details page
  const openDriverDetails = (driver) => {
    navigate(`/drivers/${driver._id}`);
  };

  // Handle add driver
  const handleAddDriver = async () => {
    try {
      setSaving(true);

      const driverData = {
        firstName: addForm.firstName,
        lastName: addForm.lastName,
        email: addForm.email,
        password: addForm.password,
        phoneNumber: addForm.phone,
        driverProfile: {
          vehicle: addForm.vehicle,
          licenseNumber: addForm.licenseNumber,
          status: 'Offline',
        },
      };

      await driversService.createDriver(driverData);
      setShowAddModal(false);
      setAddForm({
        firstName: '',
        lastName: '',
        email: '',
        password: '',
        phone: '',
        vehicle: '',
        licenseNumber: '',
      });
      fetchDrivers(pagination.page);
    } catch (err) {
      console.error('Add driver error:', err);
      alert(err.message || 'Failed to add driver');
    } finally {
      setSaving(false);
    }
  };

  // Open edit modal
  const openEditModal = (driver) => {
    setSelectedDriver(driver);
    setEditForm({
      firstName: driver.firstName || '',
      lastName: driver.lastName || '',
      email: driver.email || '',
      phone: driver.phone || '',
      vehicle: driver.driverProfile?.vehicle || '',
      licenseNumber: driver.driverProfile?.licenseNumber || '',
    });
    setShowEditModal(true);
    setActiveMenu(null);
  };

  // Handle save edit
  const handleSaveEdit = async () => {
    if (!selectedDriver) return;

    try {
      setSaving(true);
      await driversService.updateDriver(selectedDriver._id, {
        firstName: editForm.firstName,
        lastName: editForm.lastName,
        phone: editForm.phone,
        driverProfile: {
          vehicle: editForm.vehicle,
          licenseNumber: editForm.licenseNumber,
        },
      });
      setShowEditModal(false);
      setSelectedDriver(null);
      fetchDrivers(pagination.page);
    } catch (err) {
      console.error('Update driver error:', err);
      alert('Failed to update driver');
    } finally {
      setSaving(false);
    }
  };

  // Handle page change
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      fetchDrivers(newPage);
    }
  };

  // Close menus when clicking outside
  useEffect(() => {
    const handleClickOutside = () => setActiveMenu(null);
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  return (
    <div className="drivers-page-container">
      {/* Header */}
      <div className="mq-welcome-block">
        <button className="mq-back-btn" onClick={() => navigate('/dashboard')}>
          <span className="mq-back-btn-icon">
            <svg width="12" height="12" viewBox="0 0 16 16" fill="none">
              <path d="M10 12L6 8L10 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </span>
          Back Home
        </button>
        <h1 className="mq-welcome-name">Drivers</h1>
      </div>

      {/* Error message */}
      {error && (
        <div className="drivers-error">
          <span>{error}</span>
          <button onClick={() => fetchDrivers(pagination.page)}>Retry</button>
        </div>
      )}

      {/* Stats Cards */}
      <div className="drivers-stats-row">
        <div className="drivers-stat-card">
          <div className="drivers-stat-icon drivers-stat-total">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M10 10C12.0711 10 13.75 8.32107 13.75 6.25C13.75 4.17893 12.0711 2.5 10 2.5C7.92893 2.5 6.25 4.17893 6.25 6.25C6.25 8.32107 7.92893 10 10 10Z" stroke="currentColor" strokeWidth="1.5"/>
              <path d="M2.5 17.5C2.5 14.7386 5.85786 12.5 10 12.5C14.1421 12.5 17.5 14.7386 17.5 17.5" stroke="currentColor" strokeWidth="1.5"/>
            </svg>
          </div>
          <div className="drivers-stat-info">
            <span className="drivers-stat-label">Total Drivers</span>
            <span className="drivers-stat-value">{stats.total}</span>
          </div>
        </div>
        <div className="drivers-stat-card">
          <div className="drivers-stat-icon drivers-stat-online">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <circle cx="10" cy="10" r="4" fill="currentColor"/>
            </svg>
          </div>
          <div className="drivers-stat-info">
            <span className="drivers-stat-label">Online</span>
            <span className="drivers-stat-value">{stats.online}</span>
          </div>
        </div>
        <div className="drivers-stat-card">
          <div className="drivers-stat-icon drivers-stat-ontask">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M4 10L8 14L16 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <div className="drivers-stat-info">
            <span className="drivers-stat-label">On Task</span>
            <span className="drivers-stat-value">{stats.onTask}</span>
          </div>
        </div>
        <div className="drivers-stat-card">
          <div className="drivers-stat-icon drivers-stat-available">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <circle cx="10" cy="10" r="7" stroke="currentColor" strokeWidth="1.5"/>
              <path d="M10 6V10L13 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
          </div>
          <div className="drivers-stat-info">
            <span className="drivers-stat-label">Available</span>
            <span className="drivers-stat-value">{stats.available}</span>
          </div>
        </div>
        <div className="drivers-stat-card">
          <div className="drivers-stat-icon drivers-stat-offline">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <circle cx="10" cy="10" r="4" stroke="currentColor" strokeWidth="2"/>
            </svg>
          </div>
          <div className="drivers-stat-info">
            <span className="drivers-stat-label">Offline</span>
            <span className="drivers-stat-value">{stats.offline}</span>
          </div>
        </div>
      </div>

      <div className="drivers-card-wrapper">
        {/* Header Controls */}
        <div className="drivers-header-section">
          <div className="drivers-header-top">
            <h2 className="drivers-header-title">
              All Drivers {pagination.total > 0 && <span className="drivers-count">({pagination.total})</span>}
            </h2>
            <div className="drivers-header-controls">
              {/* Search */}
              <div className="drivers-search-box">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <circle cx="6.5" cy="6.5" r="5" stroke="#9ca3af" strokeWidth="1.5"/>
                  <path d="M10 10L14 14" stroke="#9ca3af" strokeWidth="1.5" strokeLinecap="round"/>
                </svg>
                <input
                  type="text"
                  className="drivers-search-input"
                  placeholder="Search drivers..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
                {search && (
                  <button className="drivers-search-clear" onClick={() => setSearch('')}>
                    ×
                  </button>
                )}
              </div>

              {/* Status Filter */}
              <select
                className="drivers-filter-select"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                {STATUS_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>

              <button className="drivers-btn-new" onClick={() => setShowAddModal(true)}>+ Add Driver</button>
            </div>
          </div>
        </div>

        {/* Table */}
        {loading && drivers.length === 0 ? (
          <div className="drivers-loading">
            <div className="drivers-spinner"></div>
            <p>Loading drivers...</p>
          </div>
        ) : drivers.length === 0 ? (
          <div className="drivers-empty">
            <p>No drivers found</p>
            <small>Try adjusting your filters or add a new driver</small>
          </div>
        ) : (
          <table className="drivers-data-table">
            <thead>
              <tr>
                <th>Driver</th>
                <th>Current Job</th>
                <th>Tasks Today</th>
                <th>On-Time %</th>
                <th>Rating</th>
                <th>Status</th>
                <th>Vehicle</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {drivers.map((driver) => (
                <tr key={driver._id} className="drivers-table-row">
                  <td className="drivers-col-name" onClick={() => openDriverDetails(driver)}>
                    <div className="drivers-name-cell">
                      <div className="drivers-avatar">
                        {driver.avatar ? (
                          <img src={driver.avatar} alt={driver.firstName} />
                        ) : (
                          driver.firstName?.[0] || 'D'
                        )}
                      </div>
                      <div className="drivers-name-info">
                        <span className="drivers-name-text">{driver.firstName} {driver.lastName}</span>
                        <span className="drivers-email-text">{driver.email}</span>
                      </div>
                    </div>
                  </td>
                  <td className="drivers-col-job">
                    {driver.currentJob ? (
                      <div className="drivers-current-job">
                        <span className="drivers-job-id">{driver.currentJob.jobId}</span>
                        <span className={`drivers-job-status ${driver.currentJob.status === 'In Transit' ? 'in-transit' : 'assigned'}`}>
                          {driver.currentJob.status}
                        </span>
                      </div>
                    ) : (
                      <span className="drivers-no-job">No active job</span>
                    )}
                  </td>
                  <td className="drivers-col-tasks">
                    <span className="drivers-tasks-count">
                      {driver.jobStats?.deliveredToday || 0}/{driver.jobStats?.todaysJobs || 0}
                    </span>
                  </td>
                  <td className="drivers-col-ontime">
                    <span className={`drivers-ontime-badge ${(driver.jobStats?.onTimeRate || 100) >= 90 ? 'good' : (driver.jobStats?.onTimeRate || 100) >= 70 ? 'warning' : 'poor'}`}>
                      {driver.jobStats?.onTimeRate || 100}%
                    </span>
                  </td>
                  <td className="drivers-col-rating">
                    <span className="drivers-rating">
                      ★ {driver.rating?.toFixed(1) || '0.0'}
                    </span>
                    <span className="drivers-reviews">({driver.totalReviews || 0})</span>
                  </td>
                  <td className="drivers-col-status">
                    <span className={`drivers-status-badge ${getStatusClass(driver.driverProfile?.status)}`}>
                      {driver.driverProfile?.status || 'Offline'}
                    </span>
                  </td>
                  <td className="drivers-col-vehicle">{driver.driverProfile?.vehicle || 'N/A'}</td>
                  <td className="drivers-col-menu">
                    <button
                      className="drivers-menu-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        setActiveMenu(activeMenu === driver._id ? null : driver._id);
                      }}
                    >
                      ⋮
                    </button>
                    {activeMenu === driver._id && (
                      <div className="drivers-menu-dropdown" onClick={(e) => e.stopPropagation()}>
                        <button onClick={() => openDriverDetails(driver)}>View Details</button>
                        <button onClick={() => openEditModal(driver)}>Edit Driver</button>
                        {driver.driverProfile?.status !== 'Online' && (
                          <button onClick={() => handleUpdateStatus(driver._id, 'Online')}>
                            Set Online
                          </button>
                        )}
                        {driver.driverProfile?.status !== 'Offline' && (
                          <button onClick={() => handleUpdateStatus(driver._id, 'Offline')}>
                            Set Offline
                          </button>
                        )}
                        <button
                          className="drivers-menu-delete"
                          onClick={() => handleDeleteDriver(driver._id)}
                        >
                          Deactivate
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {/* Pagination */}
        {drivers.length > 0 && (
          <div className="drivers-footer-section">
            <span className="drivers-footer-text">
              Page {pagination.page} of {pagination.totalPages}
            </span>
            <div className="drivers-pagination-controls">
              <button
                className="drivers-pagination-btn"
                onClick={() => handlePageChange(pagination.page - 1)}
                disabled={pagination.page <= 1}
              >
                Prev
              </button>
              {[...Array(Math.min(5, pagination.totalPages))].map((_, i) => {
                const pageNum = i + 1;
                return (
                  <button
                    key={pageNum}
                    className={`drivers-pagination-btn ${pagination.page === pageNum ? 'drivers-pagination-active' : ''}`}
                    onClick={() => handlePageChange(pageNum)}
                  >
                    {pageNum}
                  </button>
                );
              })}
              <button
                className="drivers-pagination-btn"
                onClick={() => handlePageChange(pagination.page + 1)}
                disabled={pagination.page >= pagination.totalPages}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Edit Driver Modal */}
      {showEditModal && selectedDriver && (
        <div className="drivers-modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="drivers-modal" onClick={(e) => e.stopPropagation()}>
            <div className="drivers-modal-header">
              <h3>Edit Driver</h3>
              <button className="drivers-modal-close" onClick={() => setShowEditModal(false)}>×</button>
            </div>
            <div className="drivers-modal-body">
              <div className="drivers-form">
                <div className="drivers-form-row">
                  <div className="drivers-form-group">
                    <label>First Name</label>
                    <input
                      type="text"
                      value={editForm.firstName}
                      onChange={(e) => setEditForm({ ...editForm, firstName: e.target.value })}
                    />
                  </div>
                  <div className="drivers-form-group">
                    <label>Last Name</label>
                    <input
                      type="text"
                      value={editForm.lastName}
                      onChange={(e) => setEditForm({ ...editForm, lastName: e.target.value })}
                    />
                  </div>
                </div>
                <div className="drivers-form-group">
                  <label>Email</label>
                  <input type="email" value={editForm.email} disabled />
                </div>
                <div className="drivers-form-group">
                  <label>Phone</label>
                  <input
                    type="tel"
                    value={editForm.phone}
                    onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                  />
                </div>
                <div className="drivers-form-row">
                  <div className="drivers-form-group">
                    <label>Vehicle</label>
                    <input
                      type="text"
                      value={editForm.vehicle}
                      onChange={(e) => setEditForm({ ...editForm, vehicle: e.target.value })}
                      placeholder="e.g., Van #22"
                    />
                  </div>
                  <div className="drivers-form-group">
                    <label>License Number</label>
                    <input
                      type="text"
                      value={editForm.licenseNumber}
                      onChange={(e) => setEditForm({ ...editForm, licenseNumber: e.target.value })}
                      placeholder="DL-XXXXXXXX"
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className="drivers-modal-footer">
              <button
                className="drivers-btn-primary"
                onClick={handleSaveEdit}
                disabled={saving}
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
              <button className="drivers-btn-outline" onClick={() => setShowEditModal(false)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Driver Modal */}
      {showAddModal && (
        <div className="drivers-modal-overlay" onClick={() => setShowAddModal(false)}>
          <div className="drivers-modal" onClick={(e) => e.stopPropagation()}>
            <div className="drivers-modal-header">
              <h3>Add New Driver</h3>
              <button className="drivers-modal-close" onClick={() => setShowAddModal(false)}>×</button>
            </div>
            <div className="drivers-modal-body">
              <div className="drivers-form">
                <div className="drivers-form-row">
                  <div className="drivers-form-group">
                    <label>First Name *</label>
                    <input
                      type="text"
                      value={addForm.firstName}
                      onChange={(e) => setAddForm({ ...addForm, firstName: e.target.value })}
                      placeholder="Enter first name"
                    />
                  </div>
                  <div className="drivers-form-group">
                    <label>Last Name *</label>
                    <input
                      type="text"
                      value={addForm.lastName}
                      onChange={(e) => setAddForm({ ...addForm, lastName: e.target.value })}
                      placeholder="Enter last name"
                    />
                  </div>
                </div>
                <div className="drivers-form-group">
                  <label>Email *</label>
                  <input
                    type="email"
                    value={addForm.email}
                    onChange={(e) => setAddForm({ ...addForm, email: e.target.value })}
                    placeholder="driver@example.com"
                  />
                </div>
                <div className="drivers-form-group">
                  <label>Password *</label>
                  <input
                    type="password"
                    value={addForm.password}
                    onChange={(e) => setAddForm({ ...addForm, password: e.target.value })}
                    placeholder="Minimum 8 characters"
                  />
                </div>
                <div className="drivers-form-group">
                  <label>Phone</label>
                  <input
                    type="tel"
                    value={addForm.phone}
                    onChange={(e) => setAddForm({ ...addForm, phone: e.target.value })}
                    placeholder="+1 234 567 8900"
                  />
                </div>
                <div className="drivers-form-row">
                  <div className="drivers-form-group">
                    <label>Vehicle</label>
                    <input
                      type="text"
                      value={addForm.vehicle}
                      onChange={(e) => setAddForm({ ...addForm, vehicle: e.target.value })}
                      placeholder="e.g., Van #22"
                    />
                  </div>
                  <div className="drivers-form-group">
                    <label>License Number</label>
                    <input
                      type="text"
                      value={addForm.licenseNumber}
                      onChange={(e) => setAddForm({ ...addForm, licenseNumber: e.target.value })}
                      placeholder="DL-XXXXXXXX"
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className="drivers-modal-footer">
              <button
                className="drivers-btn-primary"
                onClick={handleAddDriver}
                disabled={saving || !addForm.firstName || !addForm.lastName || !addForm.email || !addForm.password}
              >
                {saving ? 'Adding...' : 'Add Driver'}
              </button>
              <button className="drivers-btn-outline" onClick={() => setShowAddModal(false)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
